import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { moviesData } from '@/mocks/movies';
import { getHot, typeLabel } from '@/utils/movie';
import { useSeo } from '@/hooks/useSeo';
import MovieCard from '@/components/base/MovieCard';
import SectionHeader from '@/components/base/SectionHeader';

const typeTabs = [
  { label: '全部', value: 'all' },
  { label: '电视剧', value: 'tv' },
  { label: '电影', value: 'movie' },
  { label: '综艺', value: 'variety' },
];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') ?? '';

  const [input, setInput] = useState(query);
  const [activeType, setActiveType] = useState('all');

  useEffect(() => {
    setInput(query);
    setActiveType('all');
  }, [query]);

  useSeo({
    title: query ? `“${query}”的搜索结果 - 牛牛视频` : '影视搜索 - 牛牛视频',
    description: query
      ? `牛牛视频为您找到与“${query}”相关的影视内容，涵盖电视剧、电影、综艺等多种类型。`
      : '在牛牛视频搜索你想看的电视剧、电影与综艺，快速找到最新热门影视内容。',
    keywords: '牛牛视频搜索,影视搜索,电视剧,电影,综艺',
  });

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return moviesData.filter(
      (m) =>
        m.title.toLowerCase().includes(q) ||
        m.cast.some((c) => c.toLowerCase().includes(q)) ||
        m.category.toLowerCase().includes(q),
    );
  }, [query]);

  const filtered = useMemo(
    () => (activeType === 'all' ? results : results.filter((m) => m.type === activeType)),
    [results, activeType],
  );

  const hot = getHot(moviesData, 6);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    setSearchParams(q ? { q } : {});
  };

  return (
    <div className="w-full pt-16 md:pt-20">
      {/* 搜索头部 */}
      <div className="w-full bg-primary-500">
        <div className="w-full px-4 md:px-8 py-8 md:py-12">
          <nav className="flex items-center gap-1.5 text-xs md:text-sm text-primary-200">
            <Link to="/" className="hover:text-background-50 transition-colors cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-50">搜索</span>
          </nav>
          <h1 className="mt-3 text-2xl md:text-3xl font-black text-background-50">
            {query ? (
              <>
                搜索“<span className="text-accent-400">{query}</span>”
              </>
            ) : (
              '全站影视搜索'
            )}
          </h1>

          <form onSubmit={handleSubmit} className="mt-5 flex flex-col sm:flex-row gap-3 max-w-2xl">
            <div className="flex-1 flex items-center gap-2 h-12 pl-4 pr-2 rounded-full bg-background-50">
              <i className="ri-search-line text-lg text-foreground-400" />
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入影视名称、主演或题材"
                className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
              />
            </div>
            <button
              type="submit"
              className="h-12 px-7 rounded-full bg-accent-500 hover:bg-accent-600 text-accent-950 text-sm font-bold whitespace-nowrap transition-colors cursor-pointer"
            >
              搜索
            </button>
          </form>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        {query ? (
          <>
            {/* 结果统计 + 分类过滤 */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <p className="text-sm text-foreground-500">
                共找到 <span className="font-bold text-primary-600">{filtered.length}</span> 个结果
              </p>
              <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200/70">
                {typeTabs.map((tab) => (
                  <button
                    key={tab.value}
                    type="button"
                    onClick={() => setActiveType(tab.value)}
                    className={`px-3.5 py-1.5 rounded-full text-xs md:text-sm whitespace-nowrap transition-colors cursor-pointer ${
                      activeType === tab.value
                        ? 'bg-primary-500 text-background-50 font-medium'
                        : 'text-foreground-600 hover:text-primary-600'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {filtered.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 md:gap-5">
                {filtered.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <div className="py-16 md:py-20 text-center rounded-lg bg-background-100 border border-background-200/70">
                <span className="w-16 h-16 mx-auto rounded-full bg-background-200 flex items-center justify-center">
                  <i className="ri-emotion-sad-line text-3xl text-foreground-400" />
                </span>
                <h3 className="mt-5 text-lg font-bold text-foreground-900">没有找到相关影视</h3>
                <p className="mt-2 text-sm text-foreground-500">
                  换个关键词试试，或看看下面的热门推荐
                </p>
              </div>
            )}
          </>
        ) : (
          <div className="py-16 md:py-20 text-center rounded-lg bg-background-100 border border-background-200/70">
            <span className="w-16 h-16 mx-auto rounded-full bg-background-200 flex items-center justify-center">
              <i className="ri-search-2-line text-3xl text-foreground-400" />
            </span>
            <h3 className="mt-5 text-lg font-bold text-foreground-900">输入关键词开始搜索</h3>
            <p className="mt-2 text-sm text-foreground-500">
              支持按影视名称、主演或题材进行搜索
            </p>
          </div>
        )}

        {/* 热门推荐 */}
        <section className="mt-12 md:mt-16">
          <SectionHeader
            title={query && filtered.length === 0 ? '热门影视推荐' : '大家都在看'}
            subtitle="当前热度最高的影视作品"
            accent="accent"
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 md:gap-5">
            {hot.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}